import { IonModal, useIonAlert, useIonRouter } from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';

import { usePets } from '../../context/PetsContext';
import { useTutorial } from '../../context/TutorialContext';
import { useNotifications } from '../../hooks/Notifications/useNotifications';
import { useToast } from '../../hooks/UI/useToast';
import {
  getNotificationPermissionStatus,
  markNotificationPermissionPrompted,
  usePushNotifications,
} from '../../hooks/Utils/usePushNotifications';
import { debugPushNotificationsLog } from '../../hooks/Utils/usePushNotifications';
import type { NotificationRecord } from '../../types/Notifications/types';
import {
  buildNotificationFromPush,
  formatNotificationToastMessage,
  shouldDisplayNotificationToast,
} from '../../utils/notificationHelpers';
import {
  NOTIFICATION_PERMISSION_EVENTS,
  buildNotificationPermissionResolutionPayload,
  notificationPermissionEvents,
} from '../../utils/notificationPermissionEvents';
import {
  clearPostLoginNotificationPermissionPrompt,
  hasPostLoginNotificationPermissionPrompt,
} from '../../utils/notificationPermissionPrompt';
import Toast from '../UI/Toast';
import './PushNotificationHandler.scss';

const PUSH_NOTIFICATION_ENTRY_ROUTE = '/tabs/home';
const DEFAULT_NOTIFICATION_TITLE = 'fielpet';

/**
 * Componente que inicializa y maneja las Push Notifications.
 * Debe montarse cuando el usuario esta autenticado.
 * No hace nada en plataformas web.
 *
 * Caracteristicas:
 * - Solicita permisos de notificaciones solo una vez
 * - Guarda la fecha de la ultima solicitud
 * - Permite renovar la solicitud despues de 30 dias
 */
const PushNotificationHandler: React.FC = () => {
  const router = useIonRouter();
  const [presentAlert] = useIonAlert();
  const hasInitialized = useRef(false);
  const hasStartedPermissionPrompt = useRef(false);
  const [openedNotification, setOpenedNotification] = useState<NotificationRecord | null>(
    null,
  );
  const { toastState, showToast, hideToast } = useToast();
  const { startTour } = useTutorial();
  const { isValidating: isPetsValidating } = usePets();
  const { ingestPushNotification, markNotificationOpened, markNotificationSeen } =
    useNotifications();

  const emitCurrentPermissionResolution = useCallback(
    (source: 'existing_status' | 'prompt_dismissed', prompted: boolean) => {
      void (async () => {
        const status = await getNotificationPermissionStatus();
        notificationPermissionEvents.emit(
          NOTIFICATION_PERMISSION_EVENTS.FLOW_RESOLVED,
          buildNotificationPermissionResolutionPayload(status, source, prompted),
        );
      })();
    },
    [],
  );

  const handleNotificationReceived = useCallback(
    (notification: PushNotificationSchema) => {
      const normalized = ingestPushNotification(notification);

      if (shouldDisplayNotificationToast(normalized)) {
        showToast({
          message: formatNotificationToastMessage(normalized),
          color: 'success',
          duration: 4000,
        });
      }
    },
    [ingestPushNotification, showToast],
  );

  const closeOpenedNotification = useCallback(() => {
    setOpenedNotification(null);
  }, []);

  const preventModalClose = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const shouldShowNotificationTitle = useCallback((title: string | null | undefined) => {
    const normalizedTitle = title?.trim().toLowerCase() ?? '';
    return Boolean(normalizedTitle && normalizedTitle !== DEFAULT_NOTIFICATION_TITLE);
  }, []);

  const handleNotificationAction = useCallback(
    (action: ActionPerformed) => {
      if (!action.notification) return;

      const normalized = buildNotificationFromPush(action.notification);
      hideToast();
      ingestPushNotification(action.notification);
      markNotificationOpened(normalized.id, 'fcm');
      markNotificationSeen(normalized.id, 'fcm');
      router.push(PUSH_NOTIFICATION_ENTRY_ROUTE, 'none', 'replace');
      setOpenedNotification(normalized);
    },
    [
      hideToast,
      ingestPushNotification,
      markNotificationOpened,
      markNotificationSeen,
      router,
    ],
  );

  const {
    initialize,
    cleanup,
    isSupported,
    requestNotificationPermission,
    shouldRenewPermissionRequest,
  } = usePushNotifications({
    onNotificationReceived: handleNotificationReceived,
    onNotificationAction: handleNotificationAction,
  });

  const handlePermissionRequest = useCallback(async () => {
    const currentStatus = await getNotificationPermissionStatus();

    if (currentStatus === 'granted') {
      emitCurrentPermissionResolution('existing_status', false);
      return;
    }

    const shouldRenew = await shouldRenewPermissionRequest();

    if (!shouldRenew) {
      debugPushNotificationsLog(
        '[PushNotifications] Permiso ya fue solicitado recientemente',
      );
      emitCurrentPermissionResolution('existing_status', false);
      return;
    }

    presentAlert({
      header: 'Activa Notificaciones',
      subHeader: 'No te pierdas las novedades',
      message:
        'Las notificaciones te mantendran informado sobre eventos importantes de tu mascota como vacunas, consultas y mas.',
      buttons: [
        {
          text: 'Ahora no',
          role: 'cancel',
          handler: async () => {
            await markNotificationPermissionPrompted();
            emitCurrentPermissionResolution('prompt_dismissed', true);
          },
        },
        {
          text: 'Activar',
          handler: async () => {
            const granted = await requestNotificationPermission();
            if (granted) {
              presentAlert({
                header: 'Listo',
                message: 'Notificaciones activadas correctamente',
                buttons: ['OK'],
              });
            } else {
              presentAlert({
                header: 'Permiso denegado',
                message:
                  'No pudimos activar las notificaciones. Puedes intentar mas tarde.',
                buttons: ['OK'],
              });
            }
          },
        },
      ],
    });
  }, [
    emitCurrentPermissionResolution,
    presentAlert,
    requestNotificationPermission,
    shouldRenewPermissionRequest,
  ]);

  useEffect(() => {
    if (hasInitialized.current || !isSupported()) return undefined;
    hasInitialized.current = true;

    void initialize();

    return () => {
      void cleanup();
    };
  }, [cleanup, initialize, isSupported]);

  useEffect(() => {
    if (!isSupported() || hasStartedPermissionPrompt.current) return;
    if (isPetsValidating) return;
    if (startTour) return;

    const timeout = window.setTimeout(() => {
      void (async () => {
        const shouldPromptAfterLogin = hasPostLoginNotificationPermissionPrompt();
        const shouldRenew = await shouldRenewPermissionRequest();

        if (!shouldPromptAfterLogin && !shouldRenew) return;

        hasStartedPermissionPrompt.current = true;
        clearPostLoginNotificationPermissionPrompt();
        void handlePermissionRequest();
      })();
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [
    handlePermissionRequest,
    isPetsValidating,
    isSupported,
    shouldRenewPermissionRequest,
    startTour,
  ]);

  return (
    <>
      <Toast
        isOpen={toastState.isOpen}
        message={toastState.message}
        color={toastState.color}
        duration={toastState.duration}
        onDidDismiss={hideToast}
      />

      <IonModal
        isOpen={Boolean(openedNotification)}
        onDidDismiss={closeOpenedNotification}
        mode="ios"
        animated={false}
        backdropDismiss={false}
        cssClass="push-notification-modal"
      >
        {openedNotification && (
          <div
            className="push-notification-modal__viewport"
            onClick={closeOpenedNotification}
            role="presentation"
          >
            <div
              className="push-notification-modal__card"
              onClick={preventModalClose}
              role="dialog"
              aria-modal="true"
              aria-label="Detalle de notificacion"
            >
              <div className="push-notification-modal__content">
                {shouldShowNotificationTitle(openedNotification.title) ? (
                  <h2 className="push-notification-modal__title">
                    {openedNotification.title}
                  </h2>
                ) : null}
                <p
                  className={[
                    'push-notification-modal__body',
                    !shouldShowNotificationTitle(openedNotification.title)
                      ? 'push-notification-modal__body--standalone'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {openedNotification.body}
                </p>
              </div>

              <button
                type="button"
                className="push-notification-modal__action"
                onClick={closeOpenedNotification}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </IonModal>
    </>
  );
};

export default PushNotificationHandler;
